import { CellRange, DataMap } from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { FlexGridFilter } from '@grapecity/wijmo.react.grid.filter';
import TextComponent from 'components/Input/TextComponent';
import { useAuth } from 'context/AuthContext';
import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import SelectComponent from 'components/Input/SelectComponent';
import SwitchableSelectComponent from 'components/Input/SwitchableSelectComponent';
import { FlexGridXlsxConverter } from '@grapecity/wijmo.grid.xlsx';

import { AgeCategoryWithClientTypes, DmKbn } from 'constants/constants';
import { getAgeCategory } from 'utils/ageUtils';

const ClientManage = (props) => {
  const auth = useAuth();

  const restApi = useRestApi();

  const location = useLocation();
  // 履歴
  const navigate = useNavigate();
  // グリッド
  const flexGrid = useRef();

  // 顧客一覧情報
  const [clients, setClients] = useState([]);

  // 一覧の件数
  const [count, setCount] = useState(0);

  // 一覧の全件数
  const [total, setTotal] = useState(0);

  // 選択中の顧客ID
  const [targetClientCd, setTargetClientCd] = useState();

  // 顧客区分公共のみで絞るかチェック
  const [isClientTypePublicOnly, setIsClientTypePublicOnly] = useState(false);

  // 顧客検索条件
  const [searchCondition, setSearchCondition] = useState({
    TAN_CD: '',
    NAME: '',
    ADDRESS: '',
    TEL: '',
    IS_CLIENT_TYPE_PUBLIC_ONLY: isClientTypePublicOnly,
  });

  // マスタ情報
  const master = useMaster();

  // 検索処理
  const SearchClients = (condition, targetClientCd = null) => {
    restApi.post('/api/client/list', condition, (data) => {
      const _clients = data.clients.map((client) => {
        return {
          ...client,
          // 年代を追加
          AGE_CATEGORY: getAgeCategory(client.BIRTHDAY, client.CLIENT_TYPE),
        };
      });
      setTotal(data.total);
      setClients(_clients);
      setTargetClientCd(targetClientCd);
    });
  };

  // 初期表示処理
  useEffect(() => {
    // 別画面から戻ってきた場合は検索条件を復活
    let condition = location?.state?.searchCondition ?? {
      TAN_CD: auth.auth.user_id, // 担当コードで絞り込んだ状態で表示（プランナーに限らず）
      NAME: '',
      ADDRESS: '',
      TEL: '',
      IS_CLIENT_TYPE_PUBLIC_ONLY: isClientTypePublicOnly,
    };
    setSearchCondition(condition);
    // 検索
    SearchClients(condition, location?.state?.targetClientCd);
  }, []);

  // 他の画面から戻ってきた場合に顧客を選択
  useEffect(() => {
    if (targetClientCd) {
      const index = clients.indexOf(clients.find((c) => c['CLIENT_CD'] == targetClientCd));
      if (index >= 0) {
        setTimeout(() => {
          flexGrid.current.control.select(new CellRange(index), true);
        }, 10);
      }
    }
  }, [targetClientCd]);

  // コード翻訳用のマッピング
  var planerMap = new DataMap(master.planer, 'id', 'name');
  var houseMakerMap = new DataMap(master.houseMaker, 'id', 'name');
  var ageCategoryMap = new DataMap(AgeCategoryWithClientTypes, 'key', 'value');
  var dmKbnMap = new DataMap(DmKbn, 'key', 'value');
  var channelKbnMap = new DataMap(
    [...master.channelKbn, { CHANNEL_KBN: 0 }, { CHANNEL_KBN: -1 }],
    'CHANNEL_KBN',
    'CHANNEL_KBN_NM'
  );
  var channelCdMap = new DataMap(
    [...master.channelCd, { CHANNEL_CD: 0 }, { CHANNEL_CD: -1 }],
    'CHANNEL_CD',
    'CHANNEL_NM'
  );

  // 新規顧客作成ボタン
  const handleOnClickNewClient = useCallback(() => {
    navigate('/admin/clientedit', {
      state: {
        mode: 'add',
        searchCondition: searchCondition,
      },
    }),
      [navigate];
  });

  // 編集ボタン
  const handleOnClickEditClient = useCallback(() => {
    navigate('/admin/clientedit', {
      state: {
        mode: 'upd',
        targetClient: flexGrid.current.control.selectedItems[0],
        searchCondition: searchCondition,
      },
    }),
      [navigate];
  });

  // 削除ボタン
  const handleOnClickDeleteClient = useCallback(() => {
    navigate('/admin/clientedit', {
      state: {
        mode: 'del',
        targetClient: flexGrid.current.control.selectedItems[0],
        searchCondition: searchCondition,
      },
    }),
      [navigate];
  });

  // 顧客情報ボタン
  const handleOnClickClientInfo = useCallback(() => {
    navigate('/admin/ClientInfo', {
      state: {
        clientId: flexGrid.current.control.selectedItems[0]['CLIENT_CD'],
      },
    });
  }, [navigate]);

  // 検索ボタン
  const handleOnClickSearch = () => {
    const updatedSearchCondition = {
      ...searchCondition,
      IS_CLIENT_TYPE_PUBLIC_ONLY: isClientTypePublicOnly,
    };
    SearchClients(updatedSearchCondition);
  };

  // 編集処理
  const handleChange = (prop) => (target) => {
    setSearchCondition({ ...searchCondition, [prop]: target?.value ?? target?.target.value });
  };

  // Excelダウンロード
  const handleExcelDownload = () => {
    const grid = flexGrid.current.control; // FlexGridコンポーネントへの参照を取得
    const fileName = '顧客一覧.xlsx'; // ダウンロードするExcelファイルの名前

    // Excelファイルを生成し、ダウンロードする
    FlexGridXlsxConverter.save(
      grid,
      {
        includeColumns: function (column) {
          return column.header; // ヘッダー名称なしは含めない（ボタンの項目）
        },
      },
      fileName
    );
  };

  // フィルタの変更処理
  const filterApplied = (s, e) => {
    let grid = s.grid;
    setCount(grid?.collectionView?.items.length);
  };
  // フォームのsubmit処理
  const handleOnSubmitSearch = (e) => {
    e.preventDefault();
    handleOnClickSearch();
  };

  return (
    <>
      <Card className="m-0 table-container">
        <Card.Body>
          <Row>
            <Col>
              <form onSubmit={handleOnSubmitSearch}>
                <Row>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>顧客名</label>
                      <TextComponent
                        placeholder="部分一致で検索します"
                        type="text"
                        value={searchCondition.NAME}
                        onChange={handleChange('NAME')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="3">
                    <Form.Group>
                      <label>住所</label>
                      <TextComponent
                        placeholder="部分一致で検索します"
                        type="text"
                        value={searchCondition.ADDRESS}
                        onChange={handleChange('ADDRESS')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>電話番号</label>
                      <TextComponent
                        placeholder="部分一致で検索します"
                        type="text"
                        value={searchCondition.TEL}
                        onChange={handleChange('TEL')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pl-2 pr-2" md="1.5">
                    <Form.Group>
                      <br />
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isClientTypePublicOnly}
                          onChange={() => setIsClientTypePublicOnly(!isClientTypePublicOnly)}
                        ></input>
                        公共で絞る
                      </label>
                    </Form.Group>
                  </Col>
                  <Col style={{ paddingTop: '30px' }}>
                    <Button
                      variant="success"
                      className="btn-fill"
                      size="sm"
                      type="submit"
                    >
                      検索
                    </Button>
                  </Col>
                </Row>
              </form>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                variant="success"
                className="btn-fill m-2"
                size="sm"
                onClick={handleOnClickNewClient}
              >
                新規登録
              </Button>
              件数：{count}件 / 全{total}件
              <Button
                variant="success"
                className="btn-fill mb-2 mt-2 ml-4"
                size="sm"
                onClick={handleExcelDownload}
              >
                Excelダウンロード
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="table-scroll">
                <FlexGrid
                  ref={flexGrid}
                  isReadOnly={true}
                  itemsSource={clients}
                  selectionMode={'Row'}
                  headersVisibility={'Column'}
                  style={{ height: '100%' }}
                  frozenColumns={3}
                  loadedRows={(grid) => {
                    grid.rows.forEach((ele) => {
                      ele.height = 38;
                    });
                  }}
                >
                  <FlexGridFilter filterApplied={filterApplied} />
                  <FlexGridColumn header="" binding="" isReadOnly={true} width={80}>
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="success"
                            title="顧客情報"
                            onClick={handleOnClickClientInfo}
                          >
                            person
                          </Button>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="primary"
                            title="編集"
                            onClick={handleOnClickEditClient}
                          >
                            edit
                          </Button>
                        </span>
                      )}
                    />
                  </FlexGridColumn>
                  <FlexGridColumn
                    binding="LATEST_UKE_DATE"
                    header="最新の見積依頼日"
                    width={140}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="NAME_1" header="顧客名１" width={140}></FlexGridColumn>
                  <FlexGridColumn binding="CITY" header="市"></FlexGridColumn>
                  <FlexGridColumn binding="TOWN" header="町村"></FlexGridColumn>
                  <FlexGridColumn binding="ADDRESS" header="番地"></FlexGridColumn>
                  {/* <FlexGridColumn
                    binding="FIRST_INQUIRY_DAY"
                    header="初回問合せ日"
                  ></FlexGridColumn> */}
                  <FlexGridColumn
                    binding="NAME_KANA_1"
                    header="顧客名１カナ"
                    width={150}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="NAME_2" header="顧客名２" width={140}></FlexGridColumn>
                  <FlexGridColumn
                    binding="NAME_KANA_2"
                    header="顧客名２カナ"
                    width={150}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="AGE_CATEGORY"
                    dataMap={ageCategoryMap}
                    header="年代(現在)"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="POST_CODE" header="郵便番号"></FlexGridColumn>
                  <FlexGridColumn binding="PREF" header="都道府県"></FlexGridColumn>
                  <FlexGridColumn binding="TEL1" header="電話番号１">
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          {cell.item.TEL1}{' '}
                          {cell.item.TEL_RELATION_1 ? `(${cell.item.TEL_RELATION_1})` : ''}
                        </span>
                      )}
                    />
                  </FlexGridColumn>
                  <FlexGridColumn binding="TEL2" header="電話番号２">
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          {cell.item.TEL2}{' '}
                          {cell.item.TEL_RELATION_2 ? `(${cell.item.TEL_RELATION_2})` : ''}
                        </span>
                      )}
                    />
                  </FlexGridColumn>

                  <FlexGridColumn
                    binding="MAIL"
                    header="メールアドレス"
                    width={150}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="DM_KBN"
                    header="DM"
                    dataMap={dmKbnMap}
                    width={130}
                    align="left"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="BIRTHDAY" header="生年月日"></FlexGridColumn>
                  <FlexGridColumn binding="BIKO" header="備考"></FlexGridColumn>
                  <FlexGridColumn header="" binding="" isReadOnly={true} width={40}>
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="danger"
                            title="削除"
                            onClick={handleOnClickDeleteClient}
                          >
                            delete
                          </Button>
                        </span>
                      )}
                    />
                  </FlexGridColumn>
                </FlexGrid>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default ClientManage;
